import { Autocomplete, TextField } from '@mui/material'
import { FC, useCallback, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'
import { Account, Maybe, useFetchAllAccountsLazyQuery } from '../../../../generated'
import { PAGE_LIMIT } from '../../../constants'

interface GlAccountFilterProps {
  glAccountId: string | undefined
  setReportFilters: React.Dispatch<React.SetStateAction<Record<string, any>>>
}

const GlAccountFilter: FC<GlAccountFilterProps> = ({ glAccountId, setReportFilters }) => {
  const [accounts, setAccounts] = useState<Maybe<Account>[]>([])
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null)
  const [accountPage, setAccountPage] = useState(1)
  const [hasMorePages, setHasMorePages] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch] = useDebounce(searchTerm, 500)

  const [fetchAccounts, { loading }] = useFetchAllAccountsLazyQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const result = data?.fetchAllAccounts
      if (!result) return
      const { accounts: rawAccounts, pagination } = result
      const newAccounts = (rawAccounts || []) as Maybe<Account>[]
      const { page: currentPage, totalPages } = pagination || {}
      if (rawAccounts) {
        setAccounts(prev => (accountPage === 1 ? newAccounts : [...prev, ...newAccounts]))
        setHasMorePages(currentPage && totalPages ? currentPage < totalPages : false)
      }
    },
    onError() {
      setAccounts([])
      setHasMorePages(false)
    }
  })

  useEffect(() => {
    setAccounts([])
    setAccountPage(1)
    setHasMorePages(true)
  }, [debouncedSearch])

  useEffect(() => {
    if (!hasMorePages) return
    fetchAccounts({
      variables: {
        accountsInput: {
          isBankAccount: false,
          active: true,
          searchValue: debouncedSearch.trim(),
          paginationOptions: { limit: PAGE_LIMIT, page: accountPage }
        }
      }
    })
  }, [accountPage, debouncedSearch, fetchAccounts, hasMorePages])

  // Keep the selected account available in the options list even when it is not in the current search page
  useEffect(() => {
    if (!glAccountId) {
      setSelectedAccount(null)
      return
    }
    const match = (accounts.filter(Boolean) as Account[]).find(a => a.id === glAccountId)
    if (match) setSelectedAccount(match)
  }, [glAccountId, accounts])

  const getOptionLabel = (option: Account) =>
    option?.sortCode ? `(#${option.sortCode}) ${option.name || ''}` : option?.name || ''

  const options = [
    ...(selectedAccount ? [selectedAccount] : []),
    ...(accounts.filter(Boolean) as Account[]).filter(a => a.id !== selectedAccount?.id)
  ]

  const handleScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const node = event.currentTarget
      if (node.scrollTop + node.clientHeight >= node.scrollHeight - 5 && hasMorePages && !loading) {
        setAccountPage(prev => prev + 1)
      }
    },
    [hasMorePages, loading]
  )

  return (
    <Autocomplete
      options={options}
      value={selectedAccount}
      getOptionLabel={option => getOptionLabel(option as Account)}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      onChange={(_, newValue) => {
        setSelectedAccount(newValue)
        setReportFilters(prev => ({ ...prev, glAccountId: newValue?.id }))
      }}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'input') setSearchTerm(newInput)
        else if (reason === 'clear') setSearchTerm('')
      }}
      renderInput={params => <TextField {...params} label="GL Account" />}
      ListboxProps={{
        role: 'list-box',
        style: { maxHeight: '190px' },
        onScroll: handleScroll
      }}
      loading={loading}
      noOptionsText={loading ? 'Loading...' : 'No accounts'}
      sx={{ minWidth: 250, maxWidth: 400 }}
    />
  )
}

export default GlAccountFilter
